/**
 * Demo mock backend — a global `window.fetch` interceptor.
 *
 * Installed once (from index.tsx) when the tab is in demo mode. It catches every
 * call to the agency REST API (`/api/v1/*`, `/api/v2/*`) and answers it from
 * local fixtures instead of the network, so the REAL pages render with simulated
 * data and zero auth. Anything that isn't an API call (assets, gateway OAuth,
 * 3rd-party pixels) falls through to the real fetch untouched.
 *
 * The fixture resolution lives in demo-fixtures.ts.
 */
import { isNewDemoMode } from './demo-mode';
import { resolveDemoResponse } from './demo-fixtures';
import './demo-routes'; // side-effect: registers the demo route table

let installed = false;

const API_MARKERS = ['/api/v1/', '/api/v2/'];
const isApiCall = (url: string): boolean =>
  API_MARKERS.some((m) => url.indexOf(m) !== -1);

const getUrl = (input: RequestInfo | URL): string => {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.href;
  return (input as Request).url || '';
};

const getMethod = (input: RequestInfo | URL, init?: RequestInit): string => {
  if (init && init.method) return init.method.toUpperCase();
  if (typeof input !== 'string' && !(input instanceof URL)) {
    return ((input as Request).method || 'GET').toUpperCase();
  }
  return 'GET';
};

const parseBody = (init?: RequestInit): unknown => {
  try {
    if (init && typeof init.body === 'string') return JSON.parse(init.body);
  } catch {
    /* non-JSON body */
  }
  return undefined;
};

const jsonResponse = (payload: unknown, status = 200): Response =>
  new Response(JSON.stringify(payload ?? {}), {
    status,
    statusText: status === 200 ? 'OK' : 'Demo',
    headers: { 'Content-Type': 'application/json' },
  });

// A touch of latency so real loading states/skeletons show and the tour feels
// like the live product rather than instant-snapping fixtures.
const delay = (ms: number) => new Promise((r) => setTimeout(r, ms));

export const installNewDemoBackend = (): void => {
  if (installed || typeof window === 'undefined' || !window.fetch) return;
  installed = true;

  const realFetch = window.fetch.bind(window);

  window.fetch = async (
    input: RequestInfo | URL,
    init?: RequestInit
  ): Promise<Response> => {
    try {
      if (isNewDemoMode()) {
        const url = getUrl(input);
        if (url && isApiCall(url)) {
          const method = getMethod(input, init);
          const body = parseBody(init);
          const { payload, status } = resolveDemoResponse(url, method, body);
          await delay(220);
          // Some endpoints (e.g. the client-report HTML) return raw text/html,
          // not JSON — a handler signals that with a { __rawHtml } marker.
          if (payload && typeof payload === 'object' && '__rawHtml' in (payload as Record<string, unknown>)) {
            return new Response(String((payload as { __rawHtml: unknown }).__rawHtml), {
              status: status ?? 200,
              statusText: 'OK',
              headers: { 'Content-Type': 'text/html' },
            });
          }
          return jsonResponse(payload, status ?? 200);
        }
      }
    } catch {
      /* never let the interceptor break the app — fall through to real fetch */
    }
    return realFetch(input as RequestInfo, init);
  };
};
